import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import { IColourSwatchGallery } from '@models/IColourSwatchCard';
import ProductImageGallery from '@molecules/ProductImageGallery/ProductImageGallery';
import { createColourPng } from '@utility/helpers/createColourPng';
import { handleHexCode } from '@utility/helpers/handleHexCode';
import useIsTouchDevice from '@utility/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface IColourSwatchGalleryProps extends IColourSwatchGallery {
  readonly inView: boolean;
  readonly createColourImage?: boolean;
  readonly isHovered: boolean;
  readonly variant: ColourSwatchVariantEnum;
}

const ColourSwatchGallery = ({
  variant,
  colourHexCode,
  inView,
  createColourImage = true,
  mainImage,
  images,
  isHovered,
  colourCode,
  colourName,
  tag,
}: IColourSwatchGalleryProps) => {
  const colourRef = useRef<HTMLDivElement>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string>();
  const isTouchDevice = useIsTouchDevice();

  const backgroundColorStyle = useMemo(() => {
    return colourHexCode
      ? { backgroundColor: handleHexCode(colourHexCode) }
      : { backgroundImage: `url(${mainImage?.url}?height=600)` };
  }, [colourHexCode, images]);

  const fetchImageDataUrl = useCallback(async () => {
    if (isTouchDevice || !colourHexCode) return;
    try {
      const dataUrl = await createColourPng(colourRef, colourHexCode);
      setImageDataUrl(dataUrl);
    } catch (error) {
      console.error(error);
    }
  }, [colourHexCode, isTouchDevice]);
  useEffect(() => {
    if (inView && createColourImage) fetchImageDataUrl();
  }, [fetchImageDataUrl, inView, createColourImage]);

  return (
    <div
      className={classNames(
        'tw-relative tw-w-full',
        variant === ColourSwatchVariantEnum.Large
          ? 'tw-aspect-[4/5]'
          : 'tw-aspect-[2/3]'
      )}
    >
      {!isTouchDevice && (
        <motion.div
          ref={colourRef}
          className="tw-absolute tw-h-full tw-w-full tw-bg-cover tw-bg-center"
          style={backgroundColorStyle}
          animate={{ opacity: isHovered && images && images.length ? 0 : 1 }}
          exit={{ opacity: 1 }}
          initial={{ opacity: 1 }}
        >
          {imageDataUrl && (
            <img
              className="tw-h-full tw-w-full"
              src={imageDataUrl}
              alt={colourName ?? `Colour code ${colourCode}`}
            />
          )}
        </motion.div>
      )}

      {(!images || images?.length === 0) && isTouchDevice && (
        <div
          className="tw-absolute tw-h-full tw-w-full tw-bg-center"
          style={backgroundColorStyle}
        />
      )}

      {(images?.length > 0 || (mainImage && isTouchDevice)) && (
        <motion.div
          className="tw-absolute tw-h-full tw-w-full tw-bg-center"
          style={backgroundColorStyle}
          animate={{ opacity: isHovered || isTouchDevice ? 1 : 0 }}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
        >
          {!isTouchDevice && images.length === 1 && images[0] ? (
            <Image
              image={images[0]}
              aspectRatio={
                variant === ColourSwatchVariantEnum.Large ? 4 / 5 : 2 / 3
              }
              imageClasses="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-h-full tw-object-center tw-object-cover"
            />
          ) : (
            <ProductImageGallery
              showProductImage={false}
              showColourImage={isTouchDevice && !!colourHexCode}
              colourHexCode={colourHexCode}
              productImageClasses="tw-translate-y-[0] tw-h-full tw-w-full"
              imageAspectRatio={
                variant === ColourSwatchVariantEnum.Large
                  ? 'tw-aspect-[4/5]'
                  : 'tw-aspect-[2/3]'
              }
              images={
                isTouchDevice && mainImage && !colourHexCode
                  ? [mainImage, ...(images ?? [])]
                  : images
              }
              initialIndex={0}
            />
          )}
        </motion.div>
      )}

      {tag && !(variant === ColourSwatchVariantEnum.Simple) && (
        <div
          className={classNames(
            'tw-absolute tw-z-20 tw-flex tw-gap-[5px]',
            variant === ColourSwatchVariantEnum.Detailed
              ? 'tw-bottom-4 tw-left-4'
              : 'tw-bottom-2 tw-left-2'
          )}
        >
          <div className="tw-grid tw-h-[25px] tw-place-items-center tw-bg-grey-80 tw-px-2">
            <Text variant="label-12" className="tw-text-white">
              {tag}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColourSwatchGallery;
